import { EXISTING_TAGS_ORDER } from "@components/Sections/TagsSection/constants";
import { ITagResponse } from "@interfaces/responses.interface";
import {
  ICompanyTag,
  IDealTag,
  IPeopleTag,
  OptionsForTags,
} from "@interfaces/tag.interface";
import {
  IMeetingCompanyInstance,
  IMeetingDealInstance,
  IMeetingObject,
  IMeetingPersonInstance,
  MeetingObjectType,
} from "@interfaces/meeting.interface";
import { ICompany } from "@interfaces/company.interface";
import { RelationObjectType } from "@enums/relations.enum";
import { Deal } from "@app-types/common.type";

export const sortTagsByOrder = (tags: ITagResponse[]): ITagResponse[] => {
  const sortFn = (a: ITagResponse, b: ITagResponse) => {
    const indexA = EXISTING_TAGS_ORDER.indexOf(a.object_type);
    const indexB = EXISTING_TAGS_ORDER.indexOf(b.object_type);

    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  };

  const nonSuggested = tags.filter((t) => !t.is_suggested).sort(sortFn);
  const suggested = tags.filter((t) => t.is_suggested).sort(sortFn);

  return [...nonSuggested, ...suggested];
};

export { getFullNameOrEmail } from "@utils/users.util";

export function meetingObjectToTag(
  meetingObject: IMeetingObject
): IDealTag | ICompanyTag | IPeopleTag {
  // Precondition assertion: callers must pass a meeting object.
  // If a sparse array slot or nullish API result reaches here, throw an error.
  // Callers are expected to filter their inputs (see RemoveMeetingDialog).
  if (!meetingObject) {
    throw new Error("meetingObjectToTag: meetingObject is required");
  }
  const { instance, id, object_type } = meetingObject;

  switch (object_type) {
    case "deals.deal": {
      const deal = instance as IMeetingDealInstance;
      return {
        id,
        title: deal.title,
        stage_color: deal.stage.color,
        object_type: "deals.deal",
        company: null,
        stage: deal.stage || null,
      } as IDealTag;
    }

    case "companies.company": {
      const company = instance as IMeetingCompanyInstance;
      return {
        id,
        title: company.title,
        logo: null,
        logo_url: company.logo_url,
        object_type: "companies.company",
        deal: null,
      } as ICompanyTag;
    }

    case "people.person": {
      const person = instance as IMeetingPersonInstance;
      return {
        id,
        first_name: person.first_name,
        last_name: person.last_name,
        email: person.email,
        logo: "",
        object_type: "people.person",
      } as IPeopleTag;
    }

    default:
      throw new Error(`Unsupported meeting object type: ${object_type}`);
  }
}

export function objectTypeToGroup(objectType: MeetingObjectType): keyof OptionsForTags {
  switch (objectType) {
    case RelationObjectType.DEAL:
      return "deals";
    case RelationObjectType.COMPANY:
      return "companies";
    case RelationObjectType.PERSON:
      return "people";
    default:
      throw new Error(`Unsupported meeting object type: ${objectType}`);
  }
}

/**
 * Merges an array of tags (TagResponse[]) with suggested companies extracted from suggestedPeople.
 * Suggested companies (primary_company.company with is_suggested=true) are added as valid TagResponse objects.
 *
 * @param {TagResponse[]} tagResponses - Array of existing tag responses to merge.
 * @param {ICompanyTag[]} suggestedCompanies - Array of suggested non-existing companies.
 * @returns {TagResponse[]} - Combined array of tags and suggested companies as TagResponse objects.
 */
export const mergeTagsWithSuggestedTags = (
  tagResponses: ITagResponse[] | undefined,
  suggestedCompanies: ICompanyTag[] | undefined
): ITagResponse[] => {
  const suggestedCompanyTags: ITagResponse[] = [];
  const existingCompanyIds = new Set(
    (tagResponses ?? [])
      .filter((tag) => tag.object_type === "companies.company" && tag.object && "id" in tag.object)
      .map((tag) => tag.object.id)
  );

  for (const company of suggestedCompanies ?? []) {
    if (company?.is_suggested && !existingCompanyIds.has(company.id)) {
      suggestedCompanyTags.push({
        id: company.id,
        object_type: "companies.company",
        object: {
          id: company.id,
          title: company.title,
          logo: company.logo ?? null,
          logo_url: company.logo_url ?? null,
          object_type: "companies.company",
          deal: null,
        },
        created: new Date(),
        modified: new Date(),
        is_suggested: true,
        pending: false,
      });

      existingCompanyIds.add(company.id);
    }
  }

  return [...(tagResponses ?? []), ...suggestedCompanyTags];
};

/**
 * Generated tagging option for opportunity to tag company
 *
 * @param {ICompany} company - just company from DB.
 * @returns {ICompanyTag} - tagging option.
 */
export function getCompanyTaggingOption(company: ICompany): ICompanyTag {
  return {
    id: company.id,
    title: company.title,
    logo: company.logo_url,
    website: company.website,
    object_type: RelationObjectType.COMPANY,
    deal: null,
  };
}

/**
 * Generated tagging option for opportunity to tag deal
 *
 * @param {Deal} deal - just deal from DB.
 * @returns {IDealTag} - tagging option.
 */
export function getDealTaggingOption(deal: Deal): IDealTag {
  return {
    id: deal.id,
    title: deal.title,
    stage: deal.stage,
    stage_color: deal.stage.color,
    object_type: RelationObjectType.DEAL,
    company: null,
  };
}
